import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
// import { useNavigate } from "react-router-dom";
import {
  fetchCarePlanInstance,
  fetchSectionsData,
  fetchItemsData,
  deleteCarePlan,
  publishCarePlan,
  lockCarePlan,
  fetchOverviewDraftCarePlans,
  fetchDraftCarePlanData,
  unlockCarePlan,
} from "../redux/actions/careplanActions";
import { checkLockStatus } from "../services/dashboardServices";

export const useCarePlanActions = () => {
  const dispatch = useDispatch();
  // const navigate = useNavigate();
  const userProfile = useSelector((s) => s.auth.userProfile);
  const isAdmin = useSelector((s) => s.auth.isAdmin);

  const [selected, setSelected] = useState(null);
  const [modals, setModals] = useState({
    view: false,
    edit: false,
    users: false,
    publish: false,
    delete: false,
    unlock: false,
  });
  const [isLoading, setIsLoading] = useState(false);

  /* === OPENERS === */
  const openView = (plan) => {
    setSelected(plan);
    setModals((m) => ({ ...m, view: true }));
  };
  const openEdit = async (plan) => {
    /* lock → fetch → setModals.edit = true */
    if (!plan) {
      // clear out selection & close the modal
      setSelected(null);
      setModals((m) => ({ ...m, edit: false }));
      return;
    }

    if (isLoading) return;
    setIsLoading(true);

    try {
      const status = await checkLockStatus(plan.id);
      if (status.is_locked && status.locked_by.id !== userProfile.id) {
        // show locked‑by‑other modal
        setModals((m) => ({ ...m, locked: true }));
      } else {
        await dispatch(lockCarePlan(plan.id));
        await dispatch(fetchCarePlanInstance(plan.id));
        await dispatch(fetchSectionsData());
        await dispatch(fetchItemsData());
        setSelected(plan);
        dispatch({
          type: "careplan/updateCarePlanLockStatus",
          payload: {
            id: plan.id,
            is_locked: true,
            locked_by: status.locked_by,
          },
        });
        setModals((m) => ({ ...m, edit: true }));
      }
    } finally {
      setIsLoading(false);
    }
  };
  const openManageUsers = (plan) => {
    setSelected(plan);
    setModals((m) => ({ ...m, users: true }));
  };
  const openPublish = (plan) => {
    setSelected(plan);
    setModals((m) => ({ ...m, publish: true }));
  };
  const openDelete = (plan) => {
    setSelected(plan);
    setModals((m) => ({ ...m, delete: true }));
  };
  const openUnlock = (plan) => {
    setSelected(plan);
    setModals((m) => ({ ...m, unlock: true }));
  };

  /* === CLOSERS / CONFIRMERS === */
  const closeView = () => setModals((m) => ({ ...m, view: false }));
  const closeEdit = async () => {
    if (selected?.id) {
      // try {
      //   // try to release the lock on the server
      //   await dispatch(unlockCarePlan(selected.id)).unwrap();
      // } catch (err) {
      //   // if it fails (409 or anything else), we can ignore it
      //   console.warn("unlock failed (perhaps already unlocked):", err);
      // }
      // now clear our local lock flag
      dispatch({
        type: "careplan/updateCarePlanLockStatus",
        payload: { id: selected.id, is_locked: false, locked_by: null },
      });
    }

    // finally close the modal
    setModals((m) => ({ ...m, edit: false }));
  };
  const closeManageUsers = () => setModals((m) => ({ ...m, users: false }));
  const closePublish = () => setModals((m) => ({ ...m, publish: false }));
  const closeDelete = () => setModals((m) => ({ ...m, delete: false }));
  const closeUnlock = () => setModals((m) => ({ ...m, unlock: false }));

  const confirmPublish = () =>
    dispatch(publishCarePlan({ carePlanId: selected.id }))
      .unwrap()
      .then(() => {
        setModals((m) => ({ ...m, publish: false }));
        // ← refresh the overview list immediately
        dispatch(fetchOverviewDraftCarePlans());
      });
  const confirmDelete = () =>
    dispatch(deleteCarePlan({ carePlanId: selected.id, type: "draft" }))
      .unwrap()
      .then(() => {
        setModals((m) => ({ ...m, delete: false }));
        // ← and refresh it again
        dispatch(fetchOverviewDraftCarePlans());
      });
  const confirmUnlock = () => {
    dispatch(unlockCarePlan(selected.id))
      .unwrap()
      .then(() => {
        setModals((m) => ({ ...m, unlock: false }));
        // ← refresh the overview list immediately
        dispatch(fetchDraftCarePlanData());
        dispatch(fetchOverviewDraftCarePlans());
      });
  };

  /* === OTHERS === */
  const openExport = (plan) => {
    setSelected(plan);
    setModals((m) => ({ ...m, export: true }));
  };
  const openAuditLog = (plan) => {
    setSelected(plan);
    setModals((m) => ({ ...m, audit: true }));
  };

  return {
    userProfile,
    selected,
    isAdmin,
    modals,
    setModals,
    handlers: {
      onView: openView,
      onCloseView: closeView,

      onEdit: openEdit,
      onCloseEdit: closeEdit,

      onManageUsers: openManageUsers,
      onCloseManageUsers: closeManageUsers,

      onPublish: openPublish,
      onClosePublish: closePublish,
      onConfirmPublish: confirmPublish,

      onDelete: openDelete,
      onCloseDelete: closeDelete,
      onConfirmDelete: confirmDelete,

      onExport: openExport,
      onCloseExport: () => setModals((m) => ({ ...m, export: false })),
      onAuditLog: openAuditLog,
      onCloseAuditLog: () => setModals((m) => ({ ...m, audit: false })),

      onUnlock: openUnlock,
      onCloseUnlock: closeUnlock,
      onConfirmUnlock: confirmUnlock,
    },
  };
};
